// ./src/components/features/DisplayActivations/ConnectScreenInstructionsModal/ConnectScreenInstructionsModal.tsx
import React from "react";
import {
  Stack,
  Text,
  Box,
  ThemeIcon,
  Group,
  Code,
  Timeline,
} from "@mantine/core";
import { AppModal, AppIcon } from "@/components/generic";

export interface ConnectScreenInstructionsModalProps {
  /** Whether the modal is open */
  opened: boolean;

  /** Function to close the modal */
  onClose: () => void;
}

interface InstructionStep {
  number: number;
  icon: string;
  title: string;
  description: string;
  highlight?: string;
}

const steps: InstructionStep[] = [
  {
    number: 1,
    icon: "Browser",
    title: "Open a web browser",
    description:
      "Launch any web browser on your display device (TV, tablet, etc.)",
  },
  {
    number: 2,
    icon: "Link",
    title: "Navigate to the activation page",
    description: "Type the following URL in the address bar:",
    highlight: "https://app.venueboards.grassequal.com/tv",
  },
  {
    number: 3,
    icon: "Qrcode",
    title: "Scan the QR code",
    description:
      "Use your phone's camera to scan the QR code displayed on the screen.",
  },
  {
    number: 4,
    icon: "Check",
    title: "Sign in and configure",
    description:
      "Sign in to the app on your phone, select which Display Configuration to show and click the Link button. That's it!",
  },
];

export const ConnectScreenInstructionsModal: React.FC<
  ConnectScreenInstructionsModalProps
> = ({ opened, onClose }) => {
  return (
    <AppModal
      opened={opened}
      onClose={onClose}
      title="How to Connect a Screen"
      size="lg"
      actions={[
        {
          label: "Got It",
          variant: "primary",
          onClick: onClose,
        },
      ]}
    >
      <Stack gap="xl">
        {/* Subtitle has been removed from here */}

        {/* Steps */}
        <Box px="md">
          <Timeline active={4} bulletSize={30} lineWidth={3} color="blue">
            {steps.map((step) => (
              <Timeline.Item
                key={step.number}
                bullet={
                  <ThemeIcon size={30} radius="xl" variant="filled">
                    <Text size="sm" fw={700}>
                      {step.number}
                    </Text>
                  </ThemeIcon>
                }
                title={
                  <Group gap="xs" align="center">
                    <AppIcon iconName={step.icon} size={18} />
                    <Text size="md" fw={600}>
                      {step.title}
                    </Text>
                  </Group>
                }
              >
                <Text size="sm" c="dimmed" mt={4}>
                  {step.description}
                </Text>

                {/* Highlighted URL */}
                {step.highlight && (
                  <Box mt="sm">
                    <Code
                      block
                      p="sm"
                      bg="var(--mantine-color-blue-0)"
                      c="var(--mantine-color-blue-8)"
                      style={(theme) => ({
                        fontSize: "var(--mantine-font-size-sm)",
                        fontWeight: 600,
                        border: `1px solid var(--mantine-color-blue-2)`,
                        borderRadius: theme.radius.sm,
                      })}
                    >
                      {step.highlight}
                    </Code>
                  </Box>
                )}
              </Timeline.Item>
            ))}
          </Timeline>
        </Box>

        {/* Footer Note - Icon size and color updated */}
        <Box
          p="md"
          bg="var(--mantine-color-blue-0)"
          style={(theme) => ({
            borderRadius: theme.radius.md,
            border: `1px solid var(--mantine-color-blue-2)`,
          })}
        >
          <Group
            gap="sm"
            align="flex-start"
            wrap="nowrap"
            style={{ alignItems: "flex-start" }}
          >
            <AppIcon
              iconName="InfoCircle"
              size={32}
              color="var(--mantine-color-blue-8)"
            />
            <Text size="sm" c="blue.8">
              Once connected, the screen will automatically display your chosen
              content and can be managed from this dashboard.
            </Text>
          </Group>
        </Box>
      </Stack>
    </AppModal>
  );
};
